// ============================================
// Financial Configuration Engine
// ============================================
//
// Reads BrandConfig/DocBrand and generates the
// complete financial infrastructure configuration:
// Stripe product catalog, QBO chart of accounts,
// sync rules, pharmacy reconciliation rules, and
// inter-entity transaction schedules.
//
// All outputs are deterministic — same BrandConfig
// always produces the same financial config.
// ============================================

import type { DocBrand } from '../../types/docops';
import type {
  StripeProductConfig,
  StripeConfig,
  QBOAccount,
  QBOChartTemplate,
  QBOClass,
  QBOVendor,
  SyncRule,
  PharmacyReconRule,
  RevenueStream,
  InterEntityTransaction,
} from '../../types/financial';

// ── HELPERS ──

/**
 * Parse a price string like "$149/mo", "$49.99", "$199", "$99/month"
 * into an integer amount in cents.
 *
 * Strips the "$" prefix and any "/mo" or "/month" suffix,
 * parses the remaining float, and multiplies by 100.
 * Returns 0 for unparseable strings.
 */
export function parsePriceToCents(priceStr: string): number {
  if (!priceStr) return 0;

  // Strip dollar sign
  let cleaned = priceStr.replace(/^\$/, '');

  // Strip recurring suffixes
  cleaned = cleaned.replace(/\/(mo|month)$/i, '');

  // Trim whitespace
  cleaned = cleaned.trim();

  const value = parseFloat(cleaned);
  if (isNaN(value)) return 0;

  return Math.round(value * 100);
}

/**
 * Determine if a price string indicates a monthly recurring charge.
 */
function isRecurring(priceStr: string): boolean {
  return /\/(mo|month)$/i.test(priceStr.trim());
}

/**
 * Generate a stable ID from a descriptive string.
 */
function toId(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

// ── 1. STRIPE CONFIG GENERATION ──

/**
 * Generate a complete Stripe product catalog from a DocBrand.
 *
 * - Creates subscription products from enabled paid tiers
 * - Creates a consultation product if telehealth is configured
 * - Attaches metadata for downstream QBO sync
 */
export function generateStripeConfig(brand: DocBrand): StripeConfig {
  const { config } = brand;
  const products: StripeProductConfig[] = [];

  // ── Tier subscription products ──
  const tierEntries = [
    { key: 'tier1' as const, tier: config.tiers.tier1 },
    { key: 'tier2' as const, tier: config.tiers.tier2 },
    { key: 'tier3' as const, tier: config.tiers.tier3 },
  ];

  for (const { key, tier } of tierEntries) {
    if (!tier.enabled || tier.pricingModel !== 'paid') continue;
    if (!tier.price) continue;

    const priceInCents = parsePriceToCents(tier.price);
    if (priceInCents <= 0) continue;

    products.push({
      name: `${config.brandName} - ${tier.name}`,
      type: 'service',
      priceInCents,
      recurring: isRecurring(tier.price) ? { interval: 'month' } : null,
      metadata: {
        brandId: brand.id,
        revenueStream: 'subscription',
        tier: key,
        qboAccountId: 'subscription_revenue',
      },
    });
  }

  // ── Consultation product ──
  if (config.consultationFee > 0 && config.telehealthNetwork) {
    products.push({
      name: `${config.brandName} - Telehealth Consultation`,
      type: 'service',
      priceInCents: Math.round(config.consultationFee * 100),
      recurring: null,
      metadata: {
        brandId: brand.id,
        revenueStream: 'consultation',
        qboAccountId: 'consultation_revenue',
      },
    });
  }

  return {
    products,
    taxSettings: { collectTax: true },
    generatedAt: new Date().toISOString(),
  };
}

// ── 2. CHART OF ACCOUNTS GENERATION ──

/**
 * Generate a QBO Chart of Accounts template from a DocBrand.
 *
 * Always includes the core revenue, COGS, and operating expense accounts.
 * Conditionally adds accounts based on which BrandConfig fields are populated.
 */
export function generateChartOfAccounts(brand: DocBrand): QBOChartTemplate {
  const { config } = brand;
  const accounts: QBOAccount[] = [];

  // Helper to detect active features
  const hasTelehealth = !!config.telehealthNetwork;
  const hasSupplements = !!config.supplementMarginPercent || !!config.fulfillmentPartner;
  const hasPharmacy = !!config.pharmacyPartner;
  const hasTrust = !!config.trustName;
  const hasMdRetainer = config.mdRetainer > 0;
  const hasPcRetainer = config.pcRetainer > 0;

  // ── INCOME ACCOUNTS (always) ──
  accounts.push({
    name: 'Revenue',
    type: 'Income',
    parent: null,
  });
  accounts.push({
    name: 'Subscription Revenue',
    type: 'Income',
    parent: 'Revenue',
  });
  accounts.push({
    name: 'Refunds & Returns',
    type: 'Income',
    parent: 'Revenue',
    note: 'Contra-revenue account for refunds',
  });

  // ── INCOME ACCOUNTS (conditional) ──
  if (hasTelehealth) {
    accounts.push({
      name: 'Consultation Revenue',
      type: 'Income',
      parent: 'Revenue',
      conditional: 'telehealthNetwork',
    });
  }
  if (hasSupplements) {
    accounts.push({
      name: 'Supplement Revenue',
      type: 'Income',
      parent: 'Revenue',
      conditional: 'supplementMarginPercent / fulfillmentPartner',
    });
  }
  if (hasPharmacy) {
    accounts.push({
      name: 'Rx Upgrade Revenue',
      type: 'Income',
      parent: 'Revenue',
      conditional: 'pharmacyPartner',
    });
  }

  // ── COGS ACCOUNTS (always) ──
  accounts.push({
    name: 'COGS',
    type: 'COGS',
    parent: null,
  });
  accounts.push({
    name: 'AI Processing Costs',
    type: 'COGS',
    parent: 'COGS',
  });
  accounts.push({
    name: 'Payment Processing Fees',
    type: 'COGS',
    parent: 'COGS',
  });

  // ── COGS ACCOUNTS (conditional) ──
  if (hasSupplements) {
    accounts.push({
      name: 'Supplement Wholesale Cost',
      type: 'COGS',
      parent: 'COGS',
      conditional: 'supplementMarginPercent / fulfillmentPartner',
    });
    accounts.push({
      name: 'Fulfillment & Shipping',
      type: 'COGS',
      parent: 'COGS',
      conditional: 'fulfillmentPartner',
    });
  }
  if (hasPharmacy) {
    accounts.push({
      name: 'Pharmacy Partner Cost',
      type: 'COGS',
      parent: 'COGS',
      conditional: 'pharmacyPartner',
    });
  }
  if (hasTelehealth) {
    accounts.push({
      name: 'Provider Network Fees',
      type: 'COGS',
      parent: 'COGS',
      conditional: 'telehealthNetwork',
    });
  }

  // ── OPERATING EXPENSES (always) ──
  accounts.push({
    name: 'Operating Expenses',
    type: 'Expense',
    parent: null,
  });
  accounts.push({
    name: 'Cloud Infrastructure',
    type: 'Expense',
    parent: 'Operating Expenses',
  });
  accounts.push({
    name: 'Software & Tools',
    type: 'Expense',
    parent: 'Operating Expenses',
  });
  accounts.push({
    name: 'D&O Insurance',
    type: 'Expense',
    parent: 'Operating Expenses',
  });
  accounts.push({
    name: 'Professional Services',
    type: 'Expense',
    parent: 'Operating Expenses',
  });
  accounts.push({
    name: 'Marketing & Acquisition',
    type: 'Expense',
    parent: 'Operating Expenses',
  });

  // ── OPERATING EXPENSES (conditional) ──
  if (hasTrust) {
    accounts.push({
      name: 'IP License Royalty',
      type: 'Expense',
      parent: 'Operating Expenses',
      note: `Royalty at ${config.ipLicenseRoyaltyPercent}% of revenue to ${config.trustName}`,
      conditional: 'trustName',
    });
  }
  if (hasMdRetainer) {
    accounts.push({
      name: 'Medical Director Retainer',
      type: 'Expense',
      parent: 'Operating Expenses',
      note: `$${config.mdRetainer}/mo to ${config.medicalDirectorName}`,
      conditional: 'mdRetainer',
    });
  }
  if (hasPcRetainer) {
    accounts.push({
      name: 'PC Management Fee',
      type: 'Expense',
      parent: 'Operating Expenses',
      note: `$${config.pcRetainer}/mo to ${config.pcEntityName}`,
      conditional: 'pcRetainer',
    });
  }

  // ── CLASSES (from market vertical) ──
  const classes: QBOClass[] = [];
  if (config.marketVertical) {
    classes.push({ name: config.marketVertical });
  }

  // ── VENDORS (from BrandConfig partner fields) ──
  const vendors: QBOVendor[] = [];
  if (config.fulfillmentPartner) {
    vendors.push({ name: config.fulfillmentPartner, role: 'Fulfillment & Shipping' });
  }
  if (config.pharmacyPartner) {
    vendors.push({ name: config.pharmacyPartner, role: 'Pharmacy Partner' });
  }
  if (config.telehealthNetwork) {
    vendors.push({ name: config.telehealthNetwork, role: 'Telehealth Provider Network' });
  }
  if (config.cloudProvider) {
    vendors.push({ name: config.cloudProvider, role: 'Cloud Infrastructure' });
  }

  return {
    accounts,
    classes,
    vendors,
    generatedAt: new Date().toISOString(),
  };
}

// ── 3. SYNC RULES GENERATION ──

/**
 * Generate Stripe-to-QBO sync rules from a DocBrand.
 *
 * Creates one rule per Stripe event type per active revenue stream,
 * using Stripe product metadata to route transactions to the
 * correct QBO income account.
 */
export function generateSyncRules(brand: DocBrand): SyncRule[] {
  const { config } = brand;
  const rules: SyncRule[] = [];

  // Determine active revenue streams and their QBO accounts
  const activeStreams: { stream: RevenueStream; incomeAccount: string }[] = [];

  // Subscription is always active if any paid tier is enabled
  const hasActiveSubscription = [config.tiers.tier1, config.tiers.tier2, config.tiers.tier3].some(
    (t) => t.enabled && t.pricingModel === 'paid'
  );
  if (hasActiveSubscription) {
    activeStreams.push({ stream: 'subscription', incomeAccount: 'Subscription Revenue' });
  }

  // Consultation
  if (config.telehealthNetwork && config.consultationFee > 0) {
    activeStreams.push({ stream: 'consultation', incomeAccount: 'Consultation Revenue' });
  }

  // Supplement
  if (config.supplementMarginPercent || config.fulfillmentPartner) {
    activeStreams.push({ stream: 'supplement', incomeAccount: 'Supplement Revenue' });
  }

  // Rx upgrade
  if (config.pharmacyPartner) {
    activeStreams.push({ stream: 'rx_upgrade', incomeAccount: 'Rx Upgrade Revenue' });
  }

  // ── invoice.paid → each revenue stream's income account ──
  for (const { stream, incomeAccount } of activeStreams) {
    rules.push({
      id: `invoice_paid_${toId(stream)}`,
      source: 'stripe',
      eventType: 'invoice.paid',
      classification: {
        revenueStream: stream,
        qboIncomeAccount: incomeAccount,
        class: config.marketVertical || undefined,
      },
      metadataKey: 'revenueStream',
    });
  }

  // ── charge.refunded → Refunds & Returns ──
  rules.push({
    id: 'charge_refunded',
    source: 'stripe',
    eventType: 'charge.refunded',
    classification: {
      revenueStream: 'refund',
      qboIncomeAccount: 'Refunds & Returns',
      class: config.marketVertical || undefined,
    },
    metadataKey: 'revenueStream',
  });

  // ── customer.subscription.created → Subscription Revenue ──
  if (hasActiveSubscription) {
    rules.push({
      id: 'subscription_created',
      source: 'stripe',
      eventType: 'customer.subscription.created',
      classification: {
        revenueStream: 'subscription',
        qboIncomeAccount: 'Subscription Revenue',
        class: config.marketVertical || undefined,
      },
      metadataKey: 'revenueStream',
    });
  }

  // ── customer.subscription.deleted → churn tracking ──
  if (hasActiveSubscription) {
    rules.push({
      id: 'subscription_deleted',
      source: 'stripe',
      eventType: 'customer.subscription.deleted',
      classification: {
        revenueStream: 'subscription',
        qboIncomeAccount: 'Subscription Revenue',
        class: config.marketVertical || undefined,
      },
      metadataKey: 'revenueStream',
    });
  }

  return rules;
}

// ── 4. PHARMACY RECONCILIATION RULES ──

/**
 * Generate pharmacy reconciliation rules from a DocBrand.
 *
 * Returns null if `pharmacyPartner` is not set.
 * Otherwise returns a reconciliation rule with the partner info
 * and a default 70/30 revenue split (brand keeps 70%).
 */
export function generatePharmacyReconRules(brand: DocBrand): PharmacyReconRule | null {
  const { config } = brand;

  if (!config.pharmacyPartner) {
    return null;
  }

  return {
    partnerId: toId(config.pharmacyPartner),
    revenueSharePercent: 70,
    partnerSharePercent: 30,
    revenueAccount: 'Rx Upgrade Revenue',
    cogsAccount: 'Pharmacy Partner Cost',
    reconciliationFrequency: 'monthly',
  };
}

// ── 5. INTER-ENTITY TRANSACTIONS ──

/**
 * Generate inter-entity transaction schedules from a DocBrand.
 *
 * Models the recurring money flows between legal entities:
 * - LLC -> Trust: IP license royalty (percentage of revenue)
 * - LLC -> PC: management retainer
 * - LLC/PC -> MD: medical director retainer
 *
 * All amounts are in cents. `monthlyRevenueCents` is used to
 * compute the royalty payment.
 */
export function generateInterEntityTransactions(
  brand: DocBrand,
  monthlyRevenueCents: number
): InterEntityTransaction[] {
  const { config } = brand;
  const transactions: InterEntityTransaction[] = [];

  // ── LLC → Trust royalty ──
  if (config.trustName && config.ipLicenseRoyaltyPercent > 0) {
    const royaltyAmountCents = Math.round(
      (monthlyRevenueCents * config.ipLicenseRoyaltyPercent) / 100
    );
    transactions.push({
      from: config.brandEntity,
      to: config.trustName,
      type: 'royalty',
      amountCents: royaltyAmountCents,
      description: `IP license royalty — ${config.ipLicenseRoyaltyPercent}% of monthly revenue`,
      frequency: 'monthly',
      basis: `${config.ipLicenseRoyaltyPercent}% of revenue`,
    });
  }

  // ── LLC → PC retainer ──
  if (config.pcEntityName && config.pcRetainer > 0) {
    transactions.push({
      from: config.brandEntity,
      to: config.pcEntityName,
      type: 'management_fee',
      amountCents: config.pcRetainer * 100,
      description: `PC management retainer to ${config.pcEntityName}`,
      frequency: 'monthly',
    });
  }

  // ── MD retainer ──
  if (config.medicalDirectorName && config.mdRetainer > 0) {
    const mdPayor = config.pcEntityName || config.brandEntity;
    transactions.push({
      from: mdPayor,
      to: config.medicalDirectorName,
      type: 'retainer',
      amountCents: config.mdRetainer * 100,
      description: `Medical Director retainer to ${config.medicalDirectorName} (${config.mdCredentials})`,
      frequency: 'monthly',
    });
  }

  return transactions;
}
